/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect } from 'react';

/**
 * iframe 内点击不会冒泡到父 document，Semi Popover/Dropdown 的 onClickOutSide 无法触发。
 * 监听 window blur 与 iframe focusin 以关闭浮层。
 */
export function useCloseOnIframeFocus(
  active: boolean,
  onClose: () => void,
): void {
  useEffect(() => {
    if (!active) {
      return;
    }

    const handleClose = () => {
      onClose();
    };

    const handleFocusIn = () => {
      if (document.activeElement?.tagName === 'IFRAME') {
        handleClose();
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    window.addEventListener('blur', handleClose);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('blur', handleClose);
    };
  }, [active, onClose]);
}
